import type { TFunction } from "i18next";
import { formatDateTime } from "../../../lib/datetime/displayDateTime";
import Chip from "@mui/material/Chip";
import { IncidentPriorityChip, statusChipColor } from "../lib/incidentChips";
import { optionLabel } from "../../../lib/i18n/optionLabel";

export const getIncidentCols = (t: TFunction) => [
  {
    name: t("common.title"),
    field: "title",
    selector: (row: any) => row.title || t("common.na"),
    sortable: true,
  },
  {
    name: t("incidents.priority"),
    field: "priority",
    selector: (row: any) => row.priority || t("common.na"),
    sortable: true,
    cell: (row: any) => <IncidentPriorityChip incident={row} />,
  },
  {
    name: t("incidents.status"),
    field: "status",
    selector: (row: any) => row.status || t("common.na"),
    sortable: true,
    cell: (row: any) => {
      const s = statusChipColor(row.status);
      return (
        <Chip
          label={optionLabel(t, "options.incidentStatus", row.status, t("common.unknown"))}
          size="small"
          color={s}
          variant={s === "default" ? "outlined" : "filled"}
        />
      );
    },
  },
  {
    name: t("incidents.assignedTo"),
    field: "assignedTo",
    selector: (row: any) => row.assignedTo || t("common.unassigned"),
    sortable: true,
  },
  {
    name: t("incidents.group"),
    field: "assignedGroup",
    selector: (row: any) => row.assignedGroup || t("common.unassigned"),
    sortable: true,
  },
  {
    name: t("incidents.category"),
    field: "category",
    selector: (row: any) => row.category || t("common.na"),
    sortable: true,
  },
  {
    name: t("incidents.detectedAt"),
    field: "detectedAt",
    selector: (row: any) =>
      row.detectedAt ? formatDateTime(row.detectedAt) : t("common.na"),
    sortable: true,
  },
  {
    name: t("incidents.createdBy"),
    field: "createdBy",
    selector: (row: any) => row.createdBy || t("common.na"),
    sortable: true,
  },
  {
    name: t("common.createdDate"),
    field: "createdDate",
    selector: (row: any) =>
      row.createdDate ? formatDateTime(row.createdDate) : t("common.na"),
    sortable: true,
  },
];

const IncidentModel = {
  get cols() {
    return getIncidentCols(((key: string) => key) as TFunction);
  },
  createDialogOpts: (t: TFunction) => ({
    submitButtonText: t("common.create"),
    cancelButtonText: t("common.cancel"),
    title: t("incidents.createTitle"),
    content: t("incidents.createContent"),
  }),
  editDialogOpts: (t: TFunction) => ({
    submitButtonText: t("common.update"),
    cancelButtonText: t("common.cancel"),
    title: t("incidents.editTitle"),
    content: t("incidents.editContent"),
  }),
  assignDialogOpts: (t: TFunction) => ({
    submitButtonText: t("incidents.assign"),
    cancelButtonText: t("common.cancel"),
    title: t("incidents.assignTitle"),
    content: t("incidents.assignContent"),
  }),
  commentDialogOpts: (t: TFunction) => ({
    submitButtonText: t("incidents.addComment"),
    cancelButtonText: t("common.cancel"),
    title: t("incidents.commentTitle"),
    content: t("incidents.commentContent"),
  }),
  statusDialogOpts: (t: TFunction) => ({
    submitButtonText: t("incidents.updateStatus"),
    cancelButtonText: t("common.cancel"),
    title: t("incidents.statusTitle"),
    content: t("incidents.statusContent"),
  }),
  WARNING: {
    color: "warning",
    icon: "warning",
    title: "",
    content: "",
    info: "",
  },
  ERROR: {
    color: "error",
    icon: "error",
    title: "",
    content: "",
    info: "",
  },
  SUCCESS: {
    color: "success",
    icon: "success",
    title: "",
    content: "",
    info: "",
  },
  async message(type: any, title: any, content: any, info: any) {
    return new Promise((resolve) => {
      type.title = title;
      type.content = content;
      type.info = info;
      resolve(type);
    });
  },
};

export default IncidentModel;
